/** The metrics an exercise can track. */
export type MetricKey = 'reps' | 'weight' | 'time';

export const METRIC_KEYS: readonly MetricKey[] = ['reps', 'weight', 'time'];

/**
 * A timestamp from the *device* clock, used only for last-write-wins
 * comparison between two versions of the same record.
 *
 * Branded so it cannot be compared against a ServerStamp. Mixing the two is
 * not a hypothetical: doing so once made a device with a skewed clock stop
 * pulling other devices' changes entirely, and nothing but a nominal type
 * catches it, since both are plain strings at runtime.
 */
export type LocalStamp = string & { readonly __stamp: 'local' };

/**
 * PocketBase's own `updated` value. Issued by the server, so it is consistent
 * across devices and is the only safe cursor for "what changed since I last
 * pulled". Never compare it to a LocalStamp.
 */
export type ServerStamp = string & { readonly __stamp: 'server' };

export function localStamp(iso: string): LocalStamp {
  return iso as LocalStamp;
}

export function serverStamp(value: string): ServerStamp {
  return value as ServerStamp;
}

/** Metric values as entered by the user. `time` is an "mm:ss" string. */
export interface MetricValues {
  reps?: number;
  weight?: number;
  time?: string;
}

export interface Exercise {
  id: string;
  name: string;
  metrics: MetricKey[];
  clientUpdated: LocalStamp;
}

export interface Workout extends MetricValues {
  id: string;
  date: string;
  exercise: string;
  clientUpdated: LocalStamp;
}

/** Workouts keyed by exercise name. */
export type WorkoutData = Record<string, Workout[]>;

export interface Tombstone {
  id: string;
  type: 'workout';
  clientUpdated: LocalStamp;
}

// --- PocketBase records ------------------------------------------------

export interface BaseRecord {
  id: string;
  updated: ServerStamp;
}

export interface ExerciseRecord extends BaseRecord {
  user: string;
  clientId: string;
  name: string;
  metrics: MetricKey[];
  clientUpdated: LocalStamp;
  deleted: boolean;
}

export interface WorkoutRecord extends BaseRecord {
  user: string;
  clientId: string;
  exercise: string;
  date: string;
  payload: MetricValues;
  clientUpdated: LocalStamp;
  deleted: boolean;
}

/**
 * The subset of PocketBase's auth record the sync engine needs. Kept minimal
 * so the SDK's RecordModel satisfies it without a cast.
 */
export interface AuthUser {
  id: string;
}

/**
 * The contract between the app state (app.ts) and the sync engine (sync.ts).
 * Previously an untyped object literal, where a typo in a callback name would
 * have failed silently at runtime.
 */
export interface Store {
  getExercises(): Exercise[];
  setExercises(value: Exercise[]): void;
  getWorkoutData(): WorkoutData;
  setWorkoutData(value: WorkoutData): void;
  persist(): void;
  onRemoteChange(): void;
  onAuthChange(): void;
}

export type SyncStatusKind = 'idle' | 'busy' | 'ok' | 'error' | 'offline';
export type SyncStatusListener = (text: string, kind: SyncStatusKind) => void;

/** The JSON backup file format. */
export interface ExportFile {
  exercises: Exercise[];
  workoutData: WorkoutData;
  exportDate: string;
  version: string;
}
